/**
 * HolderList — Ranked list of top token holders.
 *
 * Owns its own data fetching via useHolders hook.
 * Shows rank, wallet, balance, and % of total supply.
 *
 * Story 4.4 — Task 3
 */

import { useCallback } from "react";

import { useHolders } from "../../hooks/api/use-holders";
import { copyToClipboard } from "../../lib/clipboard";
import { cn } from "../../lib/cn";
import { formatAddress } from "../../lib/format";
import { formatPercent, formatTokenAmount } from "../../lib/format-number";
import { useToast } from "../ui/Toast";

type HolderListProps = {
	tokenId: string;
};

function RankBadge({ rank }: { rank: number }) {
	if (rank === 1) return <span className="text-sm font-bold text-yellow-500">#1</span>;
	if (rank === 2) return <span className="text-sm font-bold text-gray-400">#2</span>;
	if (rank === 3) return <span className="text-sm font-bold text-amber-600">#3</span>;
	return <span className="text-sm font-medium text-pastel-gray">#{String(rank)}</span>;
}

function ShareBar({ percentage }: { percentage: number }) {
	return (
		<div className="flex items-center gap-2">
			<div className="flex-1 h-1.5 bg-pastel-light-gray rounded-full overflow-hidden max-w-[60px]">
				<div
					className="h-full bg-pastel-vibrant-blue rounded-full"
					style={{ width: `${String(Math.min(percentage, 100))}%` }}
				/>
			</div>
			<span className="text-xs text-pastel-black w-12 text-right">{formatPercent(percentage)}</span>
		</div>
	);
}

export function HolderList({ tokenId }: HolderListProps) {
	const { data, isLoading, error, refetch } = useHolders(tokenId);
	const { addToast } = useToast();

	const holders = data?.data ?? [];
	const dataSource = data?.meta?.dataSource ?? "indexer";

	const handleCopyAddress = useCallback(
		async (address: string) => {
			try {
				await copyToClipboard(address);
				addToast({ type: "success", title: "Address copied!" });
			} catch {
				addToast({ type: "error", title: "Failed to copy" });
			}
		},
		[addToast]
	);

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<p className="text-sm text-pastel-gray mb-3">Unable to load holder data</p>
				<button
					type="button"
					onClick={() => void refetch()}
					className="px-4 py-2 text-sm font-medium text-white bg-pastel-vibrant-blue rounded-lg hover:opacity-90 transition-opacity"
				>
					Retry
				</button>
			</div>
		);
	}

	if (isLoading) {
		return <HolderListSkeleton />;
	}

	if (holders.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<p className="text-sm text-pastel-gray">No holder data available</p>
			</div>
		);
	}

	return (
		<div>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[400px]">
					<thead>
						<tr className="border-b border-pastel-border">
							<th className="py-2 px-3 text-left text-xs font-medium text-pastel-gray uppercase tracking-wide w-16">
								Rank
							</th>
							<th className="py-2 px-3 text-left text-xs font-medium text-pastel-gray uppercase tracking-wide">
								Wallet
							</th>
							<th className="py-2 px-3 text-right text-xs font-medium text-pastel-gray uppercase tracking-wide">
								Balance
							</th>
							<th className="py-2 px-3 text-right text-xs font-medium text-pastel-gray uppercase tracking-wide w-40">
								Share
							</th>
						</tr>
					</thead>
					<tbody>
						{holders.map((holder, index) => (
							<tr
								key={holder.walletAddress}
								className={cn(
									"border-b border-pastel-border last:border-b-0 hover:bg-pastel-light-gray/50 transition-colors",
									index < 3 && "bg-pastel-light-gray/20"
								)}
							>
								<td className="py-3 px-3">
									<RankBadge rank={index + 1} />
								</td>
								<td className="py-3 px-3">
									<button
										type="button"
										onClick={() => void handleCopyAddress(holder.walletAddress)}
										className="font-mono text-xs text-pastel-black hover:text-pastel-vibrant-blue transition-colors"
										title={`Copy ${holder.walletAddress}`}
									>
										{formatAddress(holder.walletAddress)}
									</button>
								</td>
								<td className="py-3 px-3 text-right text-xs text-pastel-black">
									{formatTokenAmount(holder.balance)}
								</td>
								<td className="py-3 px-3">
									<ShareBar percentage={holder.percentage} />
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{dataSource === "seeded" && (
				<div className="mt-3">
					<span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
						Sample Data
					</span>
				</div>
			)}
		</div>
	);
}

export function HolderListSkeleton() {
	return (
		<div className="animate-pulse">
			<div className="border-b border-pastel-border py-2 px-3 flex gap-3">
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						key={`hth-skeleton-${String(i)}`}
						className="h-3 bg-pastel-light-gray rounded"
						style={{ width: `${40 + i * 20}px` }}
					/>
				))}
			</div>
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					key={`htr-skeleton-${String(i)}`}
					className="border-b border-pastel-border py-3 px-3 flex gap-3"
				>
					{Array.from({ length: 4 }).map((_, j) => (
						<div
							key={`htd-skeleton-${String(i)}-${String(j)}`}
							className="h-4 bg-pastel-light-gray rounded"
							style={{ width: `${40 + j * 15}px` }}
						/>
					))}
				</div>
			))}
		</div>
	);
}
